import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faVideo, faGlobe, faPodcast, faMobileScreen, faMusic } from '@fortawesome/free-solid-svg-icons';
import ResourcesSanity from './ResourcesSanity';
import './Resources.css';

const USE_SANITY = process.env.REACT_APP_USE_SANITY_RESOURCES !== 'false';

const Resources = () => {
  // Early return for Sanity version (before any hooks)
  if (USE_SANITY) {
    return <ResourcesSanity />;
  }
  
  return <LegacyResources />;
};

const LegacyResources = () => {
  // Add states to control visibility of additional cards
  const [showAllBooks, setShowAllBooks] = useState(false);
  const [showAllMusic, setShowAllMusic] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [showAllWebsites, setShowAllWebsites] = useState(false);
  const [showAllPodcasts, setShowAllPodcasts] = useState(false);
  const [showAllApps, setShowAllApps] = useState(false);
  
  // Add state to track if we're on mobile or desktop
  const [isMobile, setIsMobile] = useState(false);
  
  // Create refs for each section header
  const booksHeaderRef = useRef(null);
  const musicHeaderRef = useRef(null);
  const videosHeaderRef = useRef(null);
  const websitesHeaderRef = useRef(null);
  const podcastsHeaderRef = useRef(null);
  const appsHeaderRef = useRef(null);
  
  // Track window size and determine if we're on mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkIsMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkIsMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Sample book data - you can replace these with your actual books
  const books = [
    {
      title: "Beyond Human: Fully Identified in the New Creation",
      image: "/images/beyond-human.jpg",
      link: "https://www.amazon.com/Beyond-Human-Fully-Identified-Creation/dp/0994433557"
    },
    {
      title: "Limitless: Living as an Overcomer",
      image: "/images/limitless-nancy.jpg",
      link: "https://www.amazon.com/Limitless-Living-Overcomer-Nancy-Coen/dp/1732163863"
    },
    {
      title: "Mystical Union: Stuff They Never Told You About the Finished Work of the Cross",
      image: "/images/mystical-union.jpg",
      link: "https://www.amazon.com/Mystical-Union-Stuff-never-finished/dp/097708261X"
    },
    {
      title: "The Man Who Talked With Angels - Second Edition",
      image: "/images/angels-roland-buck.jpg",
      link: "https://www.amazon.com/Man-Who-Talked-Angels-Second/dp/B0BPWC2KJ6"
    },
    {
      title: "The Ecstasy of Loving God: Supernatural Pleasures",
      image: "/images/ecstacy.jpg",
      link: "https://www.amazon.com/Ecstasy-Loving-God-Supernatural-Pleasures/dp/0768427428/"
    },
    {
      title: "A Kingdom Call: Living as Heaven's Ambassadors",
      image: "/images/kingdom-call.jpg",
      link: "https://www.amazon.com/Kingdom-Call-Living-Heavens-Ambassadors/dp/B0FBXVJZ23/ref=sr_1_1?crid=2MP0PCL4CNGRA&dib=eyJ2IjoiMSJ9.J7O69knalfzcmpCS9m04Ctg-u_SH_SHrFLbS241wmvDGyvoJUaKmuxleMePVlVEdHmEK0a4ZqsiFHGogk6TvYk1ZheJdmHqn7UVteySnIseD2U1R3Zf9o7EQHJx5fBr_BMWk_icmr2e5iiWiz886n5YdgSHVH17EszLftXQv6WvS1YCiV9BYujvm3HDydSY_x58lTLoy9yCNAmLQRliDy6BNwgedf42O4nnI2rxUKR0.B7qdHX0xyrnB_g3yAtpil044oQXLe8IY6ifImrarGx4&dib_tag=se&keywords=a+kingdom+call&qid=1752029629&sprefix=a+kingdom+call%2Caps%2C421&sr=8-1"
    },
    {
      title: "The Final Quest: The greatest and last battle between light and darkness",
      image: "/images/final-quest.jpg",
      link: "https://www.amazon.com/Final-Quest-Rick-Joyner/dp/192937190X/"
    },
    {
      title: "The Passion Translation New Testament (2020 Edition)",
      image: "/images/passion.jpg",
      link: "https://www.amazon.com/Passion-Translation-Testament-2020-Espresso/dp/1424561698/"
    },
    {
      title: "The Heavenly Man: The Remarkable True Story of Chinese Christian Brother Yun",
      image: "/images/heavenly-man.jpg",
      link: "https://www.amazon.com/Heavenly-Man-remarkable-Chinese-Christian/dp/1903689511/"
    }
  ];

  // Sample placeholder music including the specific link
  const placeholderMusic = [
    {
      title: "Jesus Culture | Holy Spirit",
      link: "https://youtu.be/qNwnOfZ5N8A?si=xZ8e4S4uazc6I7kg",
      image: "/images/Holy-Spirit.png"
    },
    { 
      title: "So Long Shadows (YouTube)",
      link: "https://www.youtube.com/@SoLongShadows",
      image: "/images/sls.png"
    },
    { 
      title: "Jon Guerra's album: Jesus",
      link: "https://music.youtube.com/playlist?list=OLAK5uy_l9BA9cjM96wZQAUkjuv-qgRqOSY7woOnc&si=GQtHvhjiSMh4ahWk",
      image: "/images/jesus-album.jpg"
    },
    { 
      title: "Jason Upton Full Set 02/23/25",
      link: "https://youtu.be/T4pzdGNehfY?si=Y_egONuzV-p5zvys",
      image: "/images/upton.png"
    },
  ];

  // Sample placeholder videos
  const placeholderVideos = [
    {
      title: "Prophetic Insight Video 1",
      thumbnail: "/images/placeholder-video.jpg",
      link: "#"
    },
    {
      title: "Ministry Update Video 2",
      thumbnail: "/images/placeholder-video.jpg",
      link: "#"
    },
    {
      title: "Teaching Series Episode 3",
      thumbnail: "/images/placeholder-video.jpg",
      link: "#"
    },
    {
      title: "Testimony and Encouragement",
      thumbnail: "/images/placeholder-video.jpg",
      link: "#"
    }
  ];

  // Sample placeholder websites
  const placeholderWebsites = [
    {
      title: "Katie Souza Ministries",
      description: "From Prison Walls to Breakthrough Miracles",
      link: "https://katiesouza.com/",
      image: "/images/katie-souza.png"
    },
    {
      title: "The New Mystics",
      description: "John Crowder's ministry",
      link: "http://www.thenewmystics.com/",
      image: "/images/new-mystics.png"
    },
    {
      title: "Bible Gateway",
      description: "This is a searchable online bible with a lot of good resources",
      link: "http://www.biblegateway.com/",
      image: "/images/bible-gateway.jpg"
    },
    {
      title: "Aslan's Place",
      description: "So, Aslansplace is worth taking a look at, because it's always good to see what is being said in the 'big C church'",
      link: "http://aslansplace.com/",
      image: "/images/Aslan's Place.png"
    },
    {
      title: "e-Sword",
      description: "Free Bible Study for the PC, Mac, iPad, and iPhone",
      link: "http://www.e-sword.net/",
      image: "/images/e-sword.png"
    },
    {
      title: "Blue Flame 47 (Terra4ming)",
      description: "Blue Flame 47 is a community of people that is expressed as a global ministry, a local church, through non-profit activities, and publishing. Our desire is to show unconditional love to all people.",
      link: "http://www.terra4ming.com/",
      image: "/images/Blue Flame 47Template.png"
    },
    {
      title: "Harvest (Greg Laurie)",
      description: "This is the online ministry of Greg Laurie. He was involved in the 'Jesus revolution' movie",
      link: "http://www.harvest.org/",
      image: "/images/Greg Laurie.png"
    },
    {
      title: "Iris Global",
      description: "Empowered by the love of God to stop for the one in need through worship, outreach, family, education, relief, development, healing, and the arts.",
      link: "https://www.irisglobal.org/",
      image: "/images/Iris Global.png"
    },
    {
      title: "Gleanings for the Hungry",
      description: "This is a ministry which feeds people all over the world. They are based out of Fresno, Ca.",
      link: "http://gleanings.org/",
      image: "/images/Gleanings.png"
    },
    {
      title: "Christianity Today",
      description: "This is the online presence for Christianity Today magazine. Can I suggest? Peruse both Christianity Today and Charisma and think about what makes them different and what makes them similar.",
      link: "http://www.christianitytoday.com/",
      image: "/images/christianity-today.jpg"
    },
    {
      title: "Loving on Purpose",
      description: "The online presence for Danny Silk.",
      link: "http://lovingonpurpose.com/",
      image: "/images/loving-on-purpose.jpg"
    },
    {
      title: "Christianity Radio",
      description: "This is a site that provides access to multiple Christian radio stations.",
      link: "http://www.christianityradio.com/",
      image: "/images/christian-radio.jpg"
    },
    {
      title: "K-LOVE",
      description: "This is a Christian radio station.",
      link: "http://www.klove.com/",
      image: "/images/k-love.jpg"
    },
    {
      title: "Grace International",
      description: "This is a global family of churches that covers and connects pastors, plants churches, and equips leaders.",
      link: "http://www.grace-international.tv/",
      image: "/images/grace-international.jpg"
    },
    {
      title: "Kingdom Global Ministries",
      description: "The online presence of Larry Titus and Kingdom Global Ministries.",
      link: "http://kingdomglobal.com",
      image: "/images/kingdom-global.jpg"
    },
    {
      title: "Bethel Church",
      description: "The main website for Bethel Church, Redding, CA",
      link: "https://www.bethel.com",
      image: "/images/bethel.jpg"
    },
    {
      title: "Bethel Sozo",
      description: "The site for a healing ministry of Bethel",
      link: "https://bethelsozo.com",
      image: "/images/bethel-sozo.jpg"
    },
    {
      title: "Igniting Hope",
      description: "The ministry of Steve and Wendy Backlund",
      link: "https://www.ignitinghope.com",
      image: "/images/igniting-hope.jpg"
    },
    {
      title: "More Resources",
      description: "Additional ministry resources coming soon",
      link: "#"
    }
  ];

  // Sample placeholder podcasts
  const placeholderPodcasts = [
    {
      title: "Company of Burning Hearts",
      image: "/images/cobh-pod.jpg",
      link: "https://www.podomatic.com/podcasts/companyofburninghearts"
    },
    {
      title: "Weekly Devotional Series",
      link: "#"
    },
    {
      title: "Guest Speaker Interviews",
      link: "#"
    },
    {
      title: "Bible Study Commentary",
      link: "#"
    }
  ];

  // Sample placeholder apps
  const placeholderApps = [
    {
      title: "Daily Prophetic Word App",
      image: "/images/placeholder-app.jpg",
      link: "#"
    },
    {
      title: "Prayer Partner Finder",
      image: "/images/placeholder-app.jpg",
      link: "#"
    },
    {
      title: "Scripture Memorization Tool",
      image: "/images/placeholder-app.jpg",
      link: "#"
    },
    {
      title: "Ministry Event Calendar",
      image: "/images/placeholder-app.jpg",
      link: "#"
    }
  ];

  // Helper function to limit number of cards shown based on state and type
  const limitCards = (cards, showAll, type) => {
    // On mobile, just show all cards
    if (isMobile) return cards;
    
    // On desktop, limit to 8 cards (2 rows of 4) unless "show all" is true
    return showAll ? cards : cards.slice(0, 8);
  };
  
  // Toggle handler functions that also scroll to section header when collapsing
  const toggleBooks = () => {
    // If we're currently showing all and about to collapse, scroll to header
    if (showAllBooks && booksHeaderRef.current) {
      // Use block:'start' with negative margin to show content above the header
      const yOffset = -200; // pixels above the header
      const y = booksHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllBooks(!showAllBooks);
  };
  
  const toggleMusic = () => {
    if (showAllMusic && musicHeaderRef.current) {
      const yOffset = -200;
      const y = musicHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllMusic(!showAllMusic);
  };
  
  const toggleVideos = () => {
    if (showAllVideos && videosHeaderRef.current) {
      const yOffset = -200;
      const y = videosHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllVideos(!showAllVideos);
  };
  
  const toggleWebsites = () => {
    if (showAllWebsites && websitesHeaderRef.current) {
      const yOffset = -200;
      const y = websitesHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllWebsites(!showAllWebsites);
  };
  
  const togglePodcasts = () => {
    if (showAllPodcasts && podcastsHeaderRef.current) {
      const yOffset = -200;
      const y = podcastsHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllPodcasts(!showAllPodcasts);
  };
  
  const toggleApps = () => {
    if (showAllApps && appsHeaderRef.current) {
      const yOffset = -200;
      const y = appsHeaderRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setShowAllApps(!showAllApps);
  };

  return (
    <>
      {/* Books Section - Card Background */}
      <section className="section-padding resource-section card-bg">
        <Container>
          <h1 className="section-title">Resources</h1>

          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={booksHeaderRef}>
              <FontAwesomeIcon icon={faBook} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Books</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Some of our favorites</p>
            </div>
          </div>

          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(books, showAllBooks, 'books').map((book, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={book.link} target="_blank" rel="noopener noreferrer">
                    <img 
                      src={book.image} 
                      alt={book.title}
                      className="book-image"
                    />
                    <div className="book-title small">{book.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {books.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={toggleBooks}
              >
                {showAllBooks ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* Music Section - Main Background */}
      <section className="section-padding resource-section main-bg">
        <Container>
          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={musicHeaderRef}>
              <FontAwesomeIcon icon={faMusic} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Music</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Worship and Soaking</p>
            </div>
          </div>
          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(placeholderMusic, showAllMusic, 'music').map((music, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={music.link} target="_blank" rel="noopener noreferrer" title={music.title}>
                    {/* Conditionally render image or placeholder */}
                    {music.image ? (
                      <img 
                        src={music.image} 
                        alt={music.title}
                        className="book-image" // Use same class as book images
                      />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faMusic} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="book-title small">{music.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {placeholderMusic.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={toggleMusic}
              >
                {showAllMusic ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
          
          <div className="text-center mt-4">
            <p className="coming-soon-text">More music coming soon!</p>
          </div>
        </Container>
      </section>

      {/* Videos Section - Card Background */}
      <section className="section-padding resource-section card-bg">
        <Container>
          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={videosHeaderRef}>
              <FontAwesomeIcon icon={faVideo} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Videos</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Teachings and Testimonies</p>
            </div>
          </div>

          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(placeholderVideos, showAllVideos, 'videos').map((video, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={video.link} target="_blank" rel="noopener noreferrer">
                    <div className="placeholder-media">
                      <FontAwesomeIcon icon={faVideo} className="placeholder-icon" />
                    </div>
                    <div className="book-title small">{video.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {placeholderVideos.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={toggleVideos}
              >
                {showAllVideos ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
          
          <div className="text-center mt-4">
            <p className="coming-soon-text">More videos coming soon!</p>
          </div>
        </Container>
      </section>

      {/* Websites Section - Main Background */}
      <section className="section-padding resource-section main-bg">
        <Container>
          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={websitesHeaderRef}>
              <FontAwesomeIcon icon={faGlobe} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Websites</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Helpful Online Resources</p>
            </div>
          </div>

          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(placeholderWebsites, showAllWebsites, 'websites').map((website, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={website.link} target="_blank" rel="noopener noreferrer">
                    {website.image ? (
                      <img 
                        src={website.image} 
                        alt={website.title}
                        className="book-image"
                      />
                    ) : (
                    <div className="placeholder-media">
                      <FontAwesomeIcon icon={faGlobe} className="placeholder-icon" />
                    </div>
                    )}
                    <div className="book-title small text-center"> 
                      {website.title.includes('(') && website.title.includes(')') ? (
                        <>
                          <div>{website.title.substring(0, website.title.indexOf('(')).trim()}</div>
                          <div>({website.title.substring(website.title.indexOf('(') + 1, website.title.indexOf(')')).trim()})</div>
                        </>
                      ) : (
                        website.title // Fallback for titles without parentheses
                      )}
                    </div>
                    <p className="website-description small mt-1">{website.description}</p>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {placeholderWebsites.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={toggleWebsites}
              >
                {showAllWebsites ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
        </Container>
      </section>

      {/* Podcasts Section - Card Background */}
      <section className="section-padding resource-section card-bg">
        <Container>
          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={podcastsHeaderRef}>
              <FontAwesomeIcon icon={faPodcast} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Audio Podcasts</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Listen On The Go</p>
            </div>
          </div>

          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(placeholderPodcasts, showAllPodcasts, 'podcasts').map((podcast, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={podcast.link} target="_blank" rel="noopener noreferrer">
                    {podcast.image ? (
                      <img 
                        src={podcast.image} 
                        alt={podcast.title}
                        className="book-image" // Assuming same class as other cards
                      />
                    ) : (
                      <div className="placeholder-media">
                        <FontAwesomeIcon icon={faPodcast} className="placeholder-icon" />
                      </div>
                    )}
                    <div className="book-title small text-center">{podcast.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {placeholderPodcasts.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={togglePodcasts}
              >
                {showAllPodcasts ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
          
          <div className="text-center mt-4">
            <p className="coming-soon-text">Podcast episodes coming soon!</p>
          </div>
        </Container>
      </section>

      {/* Apps Section - Main Background */}
      <section className="section-padding resource-section main-bg">
        <Container>
          <div className="mb-4">
            <div className="d-flex align-items-center mb-4" ref={appsHeaderRef}>
              <FontAwesomeIcon icon={faMobileScreen} size="lg" className="me-2 text-primary" />
              <h2 className="mb-0 fs-4">Mobile Apps</h2>
              <span className="mx-2 subtitle-text small mb-0">|</span>
              <p className="subtitle-text mb-0 small">Ministry In Your Pocket</p>
            </div>
          </div>

          <Row className="book-gallery-small justify-content-start g-3">
            {limitCards(placeholderApps, showAllApps, 'apps').map((app, index) => (
              <Col key={index} xs={6} sm={6} md={3} lg={3} className="mb-3">
                <div className="book-card-small">
                  <a href={app.link} target="_blank" rel="noopener noreferrer">
                    <div className="placeholder-media">
                      <FontAwesomeIcon icon={faMobileScreen} className="placeholder-icon" />
                    </div>
                    <div className="book-title small">{app.title}</div>
                  </a>
                </div>
              </Col>
            ))}
          </Row>
          
          {placeholderApps.length > 8 && !isMobile && (
            <div className="text-center mt-4">
              <Button 
                variant="outline-primary" 
                className="more-btn"
                onClick={toggleApps}
              >
                {showAllApps ? 'Show Less' : 'Show More'}
              </Button>
            </div>
          )}
          
          <div className="text-center mt-4">
            <p className="coming-soon-text">Mobile apps coming soon!</p>
          </div>
        </Container>
      </section>
    </>
  );
};

export default Resources; 